import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface TrackingPixel {
  id: string;
  type: string;
  pixel_id: string | null;
  custom_script: string | null;
  enabled: boolean;
  placement: string;
}

export function TrackingPixels() {
  const location = useLocation();
  const isLandingPage = location.pathname.startsWith("/lp/") || location.pathname === "/survival-kit";

  const { data: pixels = [] } = useQuery({
    queryKey: ["tracking-pixels-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tracking_pixels")
        .select("id, type, pixel_id, custom_script, enabled, placement")
        .eq("enabled", true);
      if (error) throw error;
      return data as TrackingPixel[];
    },
    staleTime: 5 * 60 * 1000, // cache 5 min
  });

  useEffect(() => {
    // Filter by placement
    const activePixels = pixels.filter(
      (p) => p.placement === "all" || (p.placement === "landing_pages_only" && isLandingPage)
    );

    const scripts: HTMLElement[] = [];

    activePixels.forEach((pixel) => {
      const containerId = `tracking-pixel-${pixel.id}`;
      // Skip if already injected
      if (document.getElementById(containerId)) return;

      if (pixel.type === "meta_pixel" && pixel.pixel_id) {
        const script = document.createElement("script");
        script.id = containerId;
        script.innerHTML = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixel.pixel_id}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(script);
        scripts.push(script);

        // Also add noscript fallback
        const noscript = document.createElement("noscript");
        noscript.id = `${containerId}-ns`;
        const img = document.createElement("img");
        img.height = 1;
        img.width = 1;
        img.style.display = "none";
        img.src = `https://www.facebook.com/tr?id=${pixel.pixel_id}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.head.appendChild(noscript);
        scripts.push(noscript);
      }

      if (pixel.type === "google_analytics" && pixel.pixel_id) {
        const gtagScript = document.createElement("script");
        gtagScript.id = containerId;
        gtagScript.async = true;
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${pixel.pixel_id}`;
        document.head.appendChild(gtagScript);
        scripts.push(gtagScript);

        const configScript = document.createElement("script");
        configScript.id = `${containerId}-config`;
        configScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${pixel.pixel_id}');
        `;
        document.head.appendChild(configScript);
        scripts.push(configScript);
      }

      if (pixel.type === "google_tag_manager" && pixel.pixel_id) {
        const script = document.createElement("script");
        script.id = containerId;
        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${pixel.pixel_id}');
        `;
        document.head.appendChild(script);
        scripts.push(script);
      }

      if (pixel.type === "tiktok_pixel" && pixel.pixel_id) {
        const script = document.createElement("script");
        script.id = containerId;
        script.innerHTML = `
          !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,
          ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");
          o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
          var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
          ttq.load('${pixel.pixel_id}');
          ttq.page();
          }(window,document,'ttq');
        `;
        document.head.appendChild(script);
        scripts.push(script);
      }

      if (pixel.type === "snapchat_pixel" && pixel.pixel_id) {
        const script = document.createElement("script");
        script.id = containerId;
        script.innerHTML = `
          (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?
          a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];
          var s='script';r=t.createElement(s);r.async=!0;r.src=n;
          var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);
          })(window,document,'https://sc-static.net/scevent.min.js');
          snaptr('init','${pixel.pixel_id}',{});
          snaptr('track','PAGE_VIEW');
        `;
        document.head.appendChild(script);
        scripts.push(script);
      }

      if (pixel.type === "linkedin_pixel" && pixel.pixel_id) {
        const script = document.createElement("script");
        script.id = containerId;
        script.innerHTML = `
          _linkedin_partner_id="${pixel.pixel_id}";
          window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          (function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];
          var b=document.createElement("script");b.type="text/javascript";b.async=true;
          b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b,s);})(window.lintrk);
        `;
        document.head.appendChild(script);
        scripts.push(script);
      }

      if (pixel.type === "custom_script" && pixel.custom_script) {
        const container = document.createElement("div");
        container.id = containerId;
        container.innerHTML = pixel.custom_script;
        // Execute scripts within the custom HTML
        const scriptTags = container.querySelectorAll("script");
        scriptTags.forEach((tag) => {
          const newScript = document.createElement("script");
          if (tag.src) {
            newScript.src = tag.src;
            newScript.async = true;
          } else {
            newScript.innerHTML = tag.innerHTML;
          }
          document.head.appendChild(newScript);
          scripts.push(newScript);
        });
        // Add non-script content
        const nonScriptContent = container.innerHTML.replace(/<script[\s\S]*?<\/script>/gi, "");
        if (nonScriptContent.trim()) {
          const div = document.createElement("div");
          div.id = `${containerId}-content`;
          div.innerHTML = nonScriptContent;
          div.style.display = "none";
          document.body.appendChild(div);
          scripts.push(div);
        }
      }
    });

    // Cleanup on unmount or pixel change
    return () => {
      scripts.forEach((el) => el.remove());
    };
  }, [pixels, isLandingPage]);

  return null; // This component only handles side effects
}
